import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Car, Users, Plus, Trash2, User, Phone,
  Mail, MapPin, Globe, Check, Loader2, Truck, ChevronRight, Edit
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';

const REGIONS = [
  { id: 'hurghada', name: 'Hurghada' },
  { id: 'sharm', name: 'Sharm El Sheikh' },
  { id: 'cairo', name: 'Kairo' },
  { id: 'luxor', name: 'Luxor' },
  { id: 'aswan', name: 'Assuan' },
  { id: 'alexandria', name: 'Alexandria' },
  { id: 'marsa_alam', name: 'Marsa Alam' },
];

const VEHICLE_TYPES = [
  { id: 'economy', name: 'Economy', passengers: 4, luggage: 3 },
  { id: 'comfort', name: 'Comfort', passengers: 4, luggage: 3 },
  { id: 'business', name: 'Business', passengers: 3, luggage: 3 },
  { id: 'suv', name: 'SUV', passengers: 6, luggage: 5 },
  { id: 'van', name: 'Van', passengers: 8, luggage: 8 },
  { id: 'minibus', name: 'Minibus', passengers: 16, luggage: 16 },
  { id: 'bus', name: 'Bus', passengers: 50, luggage: 50 },
];

const LANGUAGES = [
  { id: 'ar', name: 'Arabisch' },
  { id: 'en', name: 'Englisch' },
  { id: 'de', name: 'Deutsch' },
  { id: 'ru', name: 'Russisch' },
];

export default function CompanyRegister() {
  const navigate = useNavigate();
  const { user, token, loadFromStorage } = useAuthStore();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingCompany, setExistingCompany] = useState(null);

  const [form, setForm] = useState({
    company_name: '',
    contact_person: '',
    phone: '',
    region: '',
    languages: ['ar'],
  });

  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ make: '', model: '', year: 2024, color: '', plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3 });
  const [newDriver, setNewDriver] = useState({ name: '', phone: '', email: '', assigned_vehicle_id: '' });

  useEffect(() => { loadFromStorage(); }, []);
  useEffect(() => { if (token) checkExisting(); }, [token]);

  const checkExisting = async () => {
    try {
      const res = await api.get('/company/profile');
      if (res.data) setExistingCompany(res.data);
    } catch (err) { /* no company */ }
  };

  const addVehicle = () => {
    if (!newVehicle.make || !newVehicle.model || !newVehicle.plate_number) return;
    const type = VEHICLE_TYPES.find(t => t.id === newVehicle.vehicle_type);
    setVehicles(prev => [...prev, { ...newVehicle, id: Date.now().toString(), passengers: type?.passengers || 4, luggage: type?.luggage || 3 }]);
    setNewVehicle({ make: '', model: '', year: 2024, color: '', plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3 });
    setShowAddVehicle(false);
  };

  const addDriver = () => {
    if (!newDriver.name || !newDriver.phone) return;
    setDrivers(prev => [...prev, { ...newDriver, id: Date.now().toString() }]);
    setNewDriver({ name: '', phone: '', email: '', assigned_vehicle_id: '' });
    setShowAddDriver(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/company/register', {
        ...form,
        vehicles: vehicles.map(({ id, ...v }) => v),
        drivers: drivers.map(({ id, ...d }) => d),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally { setLoading(false); }
  };

  if (!user || !token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md">
          <Building2 size={48} className="mx-auto text-orange-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Anmeldung erforderlich</h2>
          <p className="text-slate-500 mb-4">Bitte melden Sie sich an, um Ihr Unternehmen zu registrieren.</p>
          <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600">Zur Anmeldung</a>
        </div>
      </div>
    );
  }

  if (existingCompany) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="company-dashboard">
        <nav className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-2"><ArrowLeft size={18} /> Startseite</a>
              <div className="flex items-baseline gap-0.5"><span className="text-2xl font-extrabold text-orange-500">366</span><span className="text-sm font-semibold text-slate-800">Transfer</span></div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${existingCompany.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
              {existingCompany.status === 'approved' ? 'Genehmigt' : 'In Prüfung'}
            </span>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-orange-500 flex items-center justify-center"><Building2 size={28} className="text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{existingCompany.company_name}</h1>
              <p className="text-sm text-slate-500">{existingCompany.contact_person} | {existingCompany.region}</p>
            </div>
          </div>

          {/* Vehicles */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Car size={20} /> Fahrzeuge ({existingCompany.vehicles?.length || 0})</h2>
            </div>
            {existingCompany.vehicles?.length > 0 ? (
              <div className="space-y-3">
                {existingCompany.vehicles.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center"><Car size={18} className="text-slate-500" /></div>
                      <div>
                        <p className="font-medium text-slate-800">{v.make} {v.model} ({v.year})</p>
                        <p className="text-xs text-slate-500">{v.plate_number} | {v.vehicle_type} | {v.passengers} Pers.</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm">Noch keine Fahrzeuge</p>}
          </div>

          {/* Drivers */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Users size={20} /> Fahrer ({existingCompany.drivers?.length || 0})</h2>
            </div>
            {existingCompany.drivers?.length > 0 ? (
              <div className="space-y-3">
                {existingCompany.drivers.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><User size={18} className="text-orange-500" /></div>
                      <div>
                        <p className="font-medium text-slate-800">{d.name}</p>
                        <p className="text-xs text-slate-500">{d.phone}{d.assigned_vehicle_id ? ' | Fahrzeug zugewiesen' : ''}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm">Noch keine Fahrer</p>}
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md" data-testid="company-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-emerald-600" /></div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Unternehmen registriert!</h2>
          <p className="text-slate-500 mb-6">Ihr Unternehmen wird von unserem Team geprüft. Sie erhalten eine Bestätigung per E-Mail.</p>
          <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600">Zur Startseite</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-register">
      <nav className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <a href="/" className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-2"><ArrowLeft size={18} /> Zurück</a>
          <div className="flex items-baseline gap-0.5"><span className="text-2xl font-extrabold text-orange-500">366</span><span className="text-sm font-semibold text-slate-800">Transfer</span></div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center"><Building2 size={24} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Unternehmen registrieren</h1>
            <p className="text-sm text-slate-500">Fleet Management für Transportunternehmen</p>
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-2 mb-8">
          {[{n:1, label:'Unternehmen'}, {n:2, label:'Fahrzeuge'}, {n:3, label:'Fahrer'}].map(s => (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s.n ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'}`}>{s.n}</div>
              <span className={`text-sm font-medium ${step >= s.n ? 'text-slate-800' : 'text-slate-400'}`}>{s.label}</span>
              {s.n < 3 && <ChevronRight size={16} className="text-slate-300 ml-auto" />}
            </div>
          ))}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl mb-6">{error}</div>}

        {/* Step 1: Company Info */}
        {step === 1 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Firmenname *</label>
              <input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} placeholder="z.B. Cairo Transport GmbH" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-orange-400" data-testid="company-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Ansprechpartner *</label>
              <input value={form.contact_person} onChange={e => setForm(f => ({...f, contact_person: e.target.value}))} placeholder="Vollständiger Name" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-orange-400" data-testid="company-contact" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Telefon *</label>
              <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+20 xxx xxx xxxx" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-orange-400" data-testid="company-phone" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Region *</label>
              <select value={form.region} onChange={e => setForm(f => ({...f, region: e.target.value}))} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-orange-400" data-testid="company-region">
                <option value="">Region wählen</option>
                {REGIONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sprachen</label>
              <div className="flex flex-wrap gap-2">
                {LANGUAGES.map(lang => (
                  <button key={lang.id} onClick={() => setForm(f => ({...f, languages: f.languages.includes(lang.id) ? f.languages.filter(l => l !== lang.id) : [...f.languages, lang.id]}))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${form.languages.includes(lang.id) ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => { if (form.company_name && form.contact_person && form.phone && form.region) setStep(2); else setError('Bitte füllen Sie alle Pflichtfelder aus'); }}
              className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors" data-testid="company-next-step2">
              Weiter: Fahrzeuge
            </button>
          </div>
        )}

        {/* Step 2: Vehicles */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Fahrzeugflotte</h3>
              <button onClick={() => setShowAddVehicle(true)} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="add-vehicle-btn">
                <Plus size={16} /> Fahrzeug hinzufügen
              </button>
            </div>

            {vehicles.length > 0 ? (
              <div className="space-y-3 mb-6">
                {vehicles.map((v, i) => (
                  <div key={v.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center"><Car size={18} className="text-slate-500" /></div>
                      <div>
                        <p className="font-medium text-slate-800">{v.make} {v.model} ({v.year})</p>
                        <p className="text-xs text-slate-500">{v.plate_number} | {v.vehicle_type} | {v.color}</p>
                      </div>
                    </div>
                    <button onClick={() => setVehicles(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm mb-6">Fügen Sie mindestens ein Fahrzeug hinzu</p>}

            {showAddVehicle && (
              <div className="bg-orange-50 rounded-xl p-5 mb-6 space-y-3 border border-orange-200">
                <h4 className="font-semibold text-slate-800 text-sm">Neues Fahrzeug</h4>
                <div className="grid grid-cols-2 gap-3">
                  <input value={newVehicle.make} onChange={e => setNewVehicle(v => ({...v, make: e.target.value}))} placeholder="Marke (z.B. Mercedes)" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" data-testid="vehicle-make" />
                  <input value={newVehicle.model} onChange={e => setNewVehicle(v => ({...v, model: e.target.value}))} placeholder="Modell (z.B. V-Klasse)" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" data-testid="vehicle-model" />
                  <input type="number" value={newVehicle.year} onChange={e => setNewVehicle(v => ({...v, year: parseInt(e.target.value)}))} placeholder="Baujahr" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" />
                  <input value={newVehicle.color} onChange={e => setNewVehicle(v => ({...v, color: e.target.value}))} placeholder="Farbe" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" />
                  <input value={newVehicle.plate_number} onChange={e => setNewVehicle(v => ({...v, plate_number: e.target.value}))} placeholder="Kennzeichen" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" data-testid="vehicle-plate" />
                  <select value={newVehicle.vehicle_type} onChange={e => setNewVehicle(v => ({...v, vehicle_type: e.target.value}))} className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm">
                    {VEHICLE_TYPES.map(t => <option key={t.id} value={t.id}>{t.name} ({t.passengers} Pers.)</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button onClick={addVehicle} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="confirm-add-vehicle">Hinzufügen</button>
                  <button onClick={() => setShowAddVehicle(false)} className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-medium">Abbrechen</button>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-semibold hover:bg-slate-200">Zurück</button>
              <button onClick={() => setStep(3)} className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600" data-testid="company-next-step3">Weiter: Fahrer</button>
            </div>
          </div>
        )}

        {/* Step 3: Drivers */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Fahrer-Team</h3>
              <button onClick={() => setShowAddDriver(true)} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="add-driver-btn">
                <Plus size={16} /> Fahrer hinzufügen
              </button>
            </div>

            {drivers.length > 0 ? (
              <div className="space-y-3 mb-6">
                {drivers.map((d, i) => (
                  <div key={d.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><User size={18} className="text-orange-500" /></div>
                      <div>
                        <p className="font-medium text-slate-800">{d.name}</p>
                        <p className="text-xs text-slate-500">{d.phone}{d.email ? ` | ${d.email}` : ''}</p>
                      </div>
                    </div>
                    <button onClick={() => setDrivers(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm mb-6">Fügen Sie mindestens einen Fahrer hinzu</p>}

            {showAddDriver && (
              <div className="bg-orange-50 rounded-xl p-5 mb-6 space-y-3 border border-orange-200">
                <h4 className="font-semibold text-slate-800 text-sm">Neuer Fahrer</h4>
                <div className="grid grid-cols-2 gap-3">
                  <input value={newDriver.name} onChange={e => setNewDriver(d => ({...d, name: e.target.value}))} placeholder="Name" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" data-testid="driver-name" />
                  <input value={newDriver.phone} onChange={e => setNewDriver(d => ({...d, phone: e.target.value}))} placeholder="Telefon" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm" data-testid="driver-phone" />
                  <input value={newDriver.email} onChange={e => setNewDriver(d => ({...d, email: e.target.value}))} placeholder="E-Mail (optional)" className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm col-span-2" />
                </div>
                {vehicles.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Fahrzeug zuweisen</label>
                    <select value={newDriver.assigned_vehicle_id} onChange={e => setNewDriver(d => ({...d, assigned_vehicle_id: e.target.value}))} className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm">
                      <option value="">Kein Fahrzeug</option>
                      {vehicles.map(v => <option key={v.id} value={v.id}>{v.make} {v.model} ({v.plate_number})</option>)}
                    </select>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={addDriver} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="confirm-add-driver">Hinzufügen</button>
                  <button onClick={() => setShowAddDriver(false)} className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-medium">Abbrechen</button>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <h4 className="font-semibold text-sm text-slate-700 mb-2">Zusammenfassung</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><p className="text-xl font-bold text-slate-800">1</p><p className="text-xs text-slate-500">Unternehmen</p></div>
                <div><p className="text-xl font-bold text-orange-500">{vehicles.length}</p><p className="text-xs text-slate-500">Fahrzeuge</p></div>
                <div><p className="text-xl font-bold text-emerald-500">{drivers.length}</p><p className="text-xs text-slate-500">Fahrer</p></div>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-semibold hover:bg-slate-200">Zurück</button>
              <button onClick={handleSubmit} disabled={loading} className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="company-submit">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Wird gesendet...</> : 'Unternehmen registrieren'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
